import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: any) => ({
  accordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
      marginBottom: theme.spacing(2),
    },
  },
  summary: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.default,
    minHeight: theme.spacing(5),
    '& .MuiAccordionSummary-content': {
      margin: 0,
    },
    '&.Mui-expanded': {
      backgroundColor: theme.palette.background.default,
      minHeight: 'auto',
      '& .MuiAccordionSummary-content': {
        margin: 0,
      },
    },
  },
  summaryContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    '&.Mui-expanded': {
      margin: 0,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  summaryTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    wordBreak: 'break-word',
  },
  summaryIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.text.primary,
    flexShrink: 0,
  },
  summaryAmount: {
    fontSize: '18px',
    fontWeight: 700,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  expandIcon: {
    color: theme.palette.text.secondary,
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  section: {
    marginBottom: theme.spacing(3),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1.5),
    letterSpacing: '0.5px',
    wordBreak: 'break-word',
  },
  explanation: {
    fontSize: '13px',
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    marginBottom: theme.spacing(1.5),
    wordBreak: 'break-word',
  },
  breakdownRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    '&:last-child': {
      borderBottom: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  breakdownLabel: {
    fontSize: '13px',
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
  breakdownAmount: {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1.5, 0),
    marginTop: theme.spacing(1),
    borderTop: `2px solid ${theme.palette.divider}`,
    fontWeight: 700,
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  totalLabel: {
    fontSize: '14px',
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
  },
  totalAmount: {
    fontSize: '16px',
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
  },
  mainExplanation: {
    fontSize: '13px',
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  reassuranceBox: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(1.5),
  },
}));

interface EarnestSectionProps {
  earnestAmount?: number; // in cents
}

/**
 * EarnestSection - Expandable accordion showing earnest money deposit details
 *
 * Displays:
 * - Total earnest deposit ($3,500)
 * - Plain English explanation of what earnest money is
 * - Friendly reassurance about what happens if deal falls through
 * - Information about credit at closing
 *
 * All text is internationalized via i18n keys from 'closingDisclosure' namespace.
 * Amounts are displayed in cents and converted to USD currency format.
 */
const EarnestSection: React.FC<EarnestSectionProps> = ({
  earnestAmount = 350000, // Default: $3,500 in cents
}) => {
  const { t } = useTranslation('closingDisclosure');
  const classes = useStyles();

  /**
   * Format amount in cents to currency string with commas and 2 decimals
   * Example: 350000 cents → "$3,500.00"
   */
  const formatCurrency = (amountCents: number | undefined | null): string => {
    if (amountCents === undefined || amountCents === null) {
      return '$0.00';
    }
    const amountDollars = amountCents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amountDollars);
  };

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary
        expandIcon={
          <FontAwesomeIcon
            icon="chevron-down"
            className={classes.expandIcon}
          />
        }
        className={classes.summary}
      >
        <Box className={classes.summaryContent}>
          <Typography className={classes.summaryTitle}>
            <FontAwesomeIcon
              icon="handshake"
              className={classes.summaryIcon}
            />
            {t('earnest-section')}
          </Typography>
          <Typography className={classes.summaryAmount}>
            {formatCurrency(earnestAmount)}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails className={classes.details}>
        <Box>
          {/* Main Explanation */}
          <Typography className={classes.mainExplanation}>
            {t('earnest-what-is')}
          </Typography>

          {/* Amount Section */}
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {t('earnest-amount')}
            </Typography>
            <Typography className={classes.explanation}>
              {t('earnest-amount-detail')}
            </Typography>
            <Box className={classes.breakdownRow}>
              <Typography className={classes.breakdownLabel}>
                {t('earnest-title')}
              </Typography>
              <Typography className={classes.breakdownAmount}>
                {formatCurrency(earnestAmount)}
              </Typography>
            </Box>
          </Box>

          {/* Total */}
          <Box className={classes.totalRow}>
            <Typography className={classes.totalLabel}>
              {t('earnest-title')}
            </Typography>
            <Typography className={classes.totalAmount}>
              {formatCurrency(earnestAmount)}
            </Typography>
          </Box>

          {/* What Happens If Deal Falls Through - Reassuring Section */}
          <Box className={classes.reassuranceBox}>
            <Typography className={classes.sectionTitle}>
              {t('earnest-when')}
            </Typography>
            <Typography className={classes.explanation}>
              {t('earnest-if-deal-falls')}
            </Typography>
          </Box>

          {/* Credit at Closing */}
          <Box className={classes.section} sx={{ marginTop: 2 }}>
            <Typography className={classes.sectionTitle}>
              {t('earnest-why')}
            </Typography>
            <Typography className={classes.explanation}>
              {t('legal-earnest-note')}
            </Typography>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default EarnestSection;
